import { Box, Slider } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import Cropper from 'react-easy-crop';
import { isMobile, isTablet } from 'react-device-detect';

import { Button, Spacer } from 'theSource/components';
import {
  DisplayPicturePaper,
  CropContainer,
  InputFileBox,
} from './DisplayPictureStyled';
import { DisplayAvatarIcon } from 'enrollment/features/Shared/DisplayProfilePicture/DisplayAvatarIcon';
import {
  chooseDisplayPicture,
  storeCroppedImage,
  resetProfileUpload,
} from '../Avatar/AvatarActions';
import { SkipProfileSelection } from '../ManageProfile/SkipProfileSelection';
import { VALID_UPLOAD_IMAGE_TYPES } from '../../../app/constants';

interface DisplayPictureDataProps {
  scope?: string;
  scrolled?: string;
}

export const DisplayPictureData = ({
  scope,
  scrolled,
}: DisplayPictureDataProps) => {
  const [crop, setCrop] = useState<any>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState<number>(0.4);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<null>(null);
  const isSmallScreen = isMobile || isTablet;
  const [isFocused, setIsFocused] = useState(false);

  const { t } = useTranslation('pages.enrollmentDashboard');
  const dispatch = useDispatch();
  const chosenDisplay = useSelector(
    (state: any) => state.enrollment.chooseAvatar?.chosenDisplay
  );
  const objectURL = useSelector(
    (state: any) => state.enrollment?.chooseAvatar?.objectURL
  );
  const croppedImagePath = useSelector(
    (state: any) => state.enrollment?.chooseAvatar?.croppedImagePath
  );
  const isCropped = useSelector(
    (state: any) => state.enrollment?.chooseAvatar?.isCropped
  );

  const displayImage = (event: any) => {
    const selectedFile = event.target.files[0];
    const fileSize = selectedFile.size;
    const fileMb = fileSize / 1024 ** 2;
    if (fileMb > 3) {
      alert('Please upload an image less than 3MP');
      return;
    }
    dispatch(chooseDisplayPicture(selectedFile));
  };
  const saveCroppedImage = (croppedImage: any) => {
    dispatch(storeCroppedImage(croppedImage));
  };
  const onCropChange = (crop: object) => {
    setCrop(crop);
  };
  const onCropComplete = (croppedArea: any, croppedAreaPixels: any) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };
  const onZoomChange = (zoom: any) => {
    setZoom(zoom);
  };
  const getCroppedImg = (imageSrc: any, pixelCrop: any) => {
    const canvas = document.createElement('canvas');
    if (canvas == null) {
      return;
    }
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;
    const circleCtx = canvas.getContext('2d');
    if (circleCtx == null) {
      return;
    }
    circleCtx.fillStyle = 'rgba(0,0,0,0.1)';
    circleCtx.save();
    circleCtx.beginPath();
    circleCtx.arc(
      pixelCrop.width / 2,
      pixelCrop.width / 2,
      pixelCrop.width / 2,
      0,
      Math.PI * 2,
      false
    );
    circleCtx.strokeStyle = '#B7C3E1';
    circleCtx.stroke();
    circleCtx.clip();
    const image = new Image();
    image.src = imageSrc;
    image.onload = () => {
      circleCtx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        pixelCrop.width,
        pixelCrop.height
      );
      circleCtx.restore();
      saveCroppedImage(canvas.toDataURL('image/jpeg'));
    };
  };

  const showCroppedImage = async () => {
    await getCroppedImg(objectURL, croppedAreaPixels);
  };

  const changeImage = () => {
    dispatch(resetProfileUpload());
  };
  return (
    <>
      <div>
        <h2 className="headline-3" id="picture-title">
          {t('displayPictureTitle')}
        </h2>
        <DisplayPicturePaper
          data-testid="displayPicturePaper"
          scrolled={scrolled}
        >
          {scope === 'subscriber' && chosenDisplay === 'picture' ? (
            <div>
              <div>
                {isCropped ? (
                  <Box
                    component="img"
                    src={croppedImagePath}
                    alt="cropped"
                    sx={{ width: '120px', borderRadius: '60px' }}
                  ></Box>
                ) : (
                  <div style={{ padding: '0 10px' }}>
                    <CropContainer>
                      <Cropper
                        minZoom={0.4}
                        image={objectURL}
                        crop={crop}
                        zoom={zoom}
                        cropShape="round"
                        showGrid={false}
                        aspect={1}
                        restrictPosition={false}
                        onCropChange={onCropChange}
                        onCropComplete={onCropComplete}
                        onZoomChange={onZoomChange}
                      />
                    </CropContainer>
                    <Slider
                      value={zoom}
                      min={0.4}
                      max={3}
                      step={0.1}
                      aria-labelledby="Zoom"
                      onChange={(e, zoom) => onZoomChange(zoom)}
                    />
                  </div>
                )}
              </div>
              {isCropped ? (
                <>
                  <Spacer height={3} />
                  <ps-button
                    label={t('change')}
                    level="secondary"
                    label-class="pt-2"
                    id="change-btn"
                    onClick={() => changeImage()}
                    data-testid="change-button"
                    other-data='aria-describedby="picture-title"'
                  ></ps-button>
                </>
              ) : (
                <Button
                  id="done-btn"
                  label={t('done')}
                  variant="tertiary"
                  onClick={() => showCroppedImage()}
                  data-testid="done-button"
                ></Button>
              )}
            </div>
          ) : (
            <div>
              <DisplayAvatarIcon />
              {scope === 'subscriber' && (
                <label
                  htmlFor="profile-picture-input"
                  className={`btn ${isFocused ? 'focus' : ''}`} // Dynamically add the "focus" class
                >
                  <InputFileBox
                    type="file"
                    id="profile-picture-input"
                    accept={VALID_UPLOAD_IMAGE_TYPES}
                    onChange={displayImage}
                    onFocus={() => setIsFocused(true)} // Add focus handler
                    onBlur={() => setIsFocused(false)} // Add blur handler
                  />
                  {t('uploadPicture')}
                </label>
              )}
            </div>
          )}
        </DisplayPicturePaper>
      </div>
      <div className="d-flex justify-content-center pb-3">
        {!isSmallScreen && scope === 'subscriber' && (
          <SkipProfileSelection navigateTo="profile" />
        )}
      </div>
    </>
  );
};
